import { X, Download, ExternalLink, Check } from "lucide-react";
import IsometricFrame from "../elements/isometric-frame";
import { usePanel } from "@/context/panel-context";
import { Button } from "../ui/button";
import { usePanelProps } from "@/context/panel-props-context";
import { config } from "@/config";
import UnifiedModelLayout from "./unified-model-layout";
import { Switch } from "../ui/switch";
import { useLocation, useNavigate } from "react-router";
import React, { Suspense, useState } from "react";
import ErrorFallback from "../elements/error-fallback";
import { ErrorBoundary } from "react-error-boundary";
import { ClipboardCopy } from "lucide-react";
import { toast } from "sonner";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "../ui/tooltip";
import { MarkdownSkeleton } from "../elements/markdown-skeleton";
import Markdown, { MARKDOWN_MAX_RENDER_CHARS } from "../elements/markdown-renderer";
import { renderToStaticMarkup } from "react-dom/server";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
// Minimal, self-contained stylesheet for the "Open markdown in new tab" page, so
// the standalone tab renders the FRD readably without the app's Tailwind bundle.
const MARKDOWN_TAB_CSS = `
  :root { color-scheme: light; }
  body { margin: 0; background: #f6f8fa; }
  .markdown-body { max-width: 900px; margin: 0 auto; padding: 40px 24px 80px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
    font-size: 15px; line-height: 1.65; color: #1f2328; background: #fff;
    border: 1px solid #d0d7de; border-radius: 8px; margin-top: 24px; }
  .markdown-body h1, .markdown-body h2 { border-bottom: 1px solid #d8dee4; padding-bottom: .3em; margin-top: 1.5em; }
  .markdown-body h1 { font-size: 1.9em; } .markdown-body h2 { font-size: 1.5em; }
  .markdown-body h3 { font-size: 1.25em; margin-top: 1.4em; }
  .markdown-body code { background: rgba(175,184,193,.2); padding: .2em .4em; border-radius: 6px; font-size: 85%; }
  .markdown-body pre { background: #f6f8fa; padding: 16px; border-radius: 6px; overflow: auto; }
  .markdown-body pre code { background: none; padding: 0; }
  .markdown-body table { border-collapse: collapse; width: 100%; margin: 1em 0; }
  .markdown-body th, .markdown-body td { border: 1px solid #d0d7de; padding: 6px 13px; }
  .markdown-body th { background: #f6f8fa; }
  .markdown-body blockquote { border-left: .25em solid #d0d7de; padding: 0 1em; color: #656d76; margin: 0; }
  .markdown-body ul, .markdown-body ol { padding-left: 1.6em; }
  .markdown-body a { color: #0969da; }
`;
const RenderMarkdown = React.memo(function MarkdownRenderer({
  content,
}: {
  content: string;
}) {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Suspense fallback={<MarkdownSkeleton />}>
        <Markdown content={content} />
      </Suspense>
    </ErrorBoundary>
  );
});

export default function RightPanelLayout() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { panelType, setPanelType } = usePanel();
  const { panelProps } = usePanelProps();
  const params = new URLSearchParams(search);
  const unified_model_mode = params.get("unified_model_mode");
  const isUnifiedModelDiagramModeEnabled = unified_model_mode === "diagram";
  const message = panelProps?.message as string;
  // BREEZEAI-785: standalone HTML render of the functional-document, if the
  // agents service produced one (document.metadata.htmlContent[0].html).
  const htmlContent = panelProps?.htmlContent;
  // BREEZEAI-1125: when the preview is truncated (markdown-renderer shows the
  // "Preview truncated — download the file..." banner), the Open button must
  // not let the user try to open a document that cannot be fully previewed.
  const isPreviewTruncated =
    typeof message === "string" && message.length > MARKDOWN_MAX_RENDER_CHARS;
  const [copied, setCopied] = useState(false);
  const [forceOpen, setForceOpen] = useState(false);

  // In-app preview for uploaded documents so images / PDFs open in a viewer
  // instead of triggering a browser download from the S3 Content-Disposition
  // header (BREEZEAI-793 Issue 4). `panelProps.message` carries the signed URL,
  // `panelProps.mode` distinguishes 'image' vs 'pdf'.
  const mediaUrl = typeof message === "string" ? message : "";
  const mediaMode = panelProps?.mode;
  const MediaPreview =
    mediaMode === "pdf" ? (
      <iframe
        src={mediaUrl}
        title="Document preview"
        className="w-full h-full border-0 bg-white"
      />
    ) : (
      <div className="w-full h-full flex items-center justify-center bg-black/60 overflow-auto p-4">
        <img
          src={mediaUrl}
          alt="Document preview"
          className="max-w-full max-h-full object-contain"
        />
      </div>
    );

  const RIGHT_PANEL = {
    UNIFIED_MODEL: <UnifiedModelLayout />,
    DIAGRAM_TOOL: <IsometricFrame />,
    GHERKIN_SCRIPT: <RenderMarkdown content={message} />,
    // Both Markdown and HTML artifacts render through <RenderMarkdown>. The
    // view handlers wrap HTML-mode content in a ```html fence before setting
    // `message`, so the markdown renderer's HTML code-block UI (with its own
    // Open/Copy toolbar) surfaces it; Markdown-mode content renders inline as
    // the FRD.
    MARKDOWN_RENDER: <RenderMarkdown content={message} />,
    MEDIA_PREVIEW: MediaPreview,
  };

  const handleOpenVisualEditor = () => {
    if (
      panelProps?.mode === "diagram" &&
      panelProps?.messageId &&
      panelProps?.uuid
    ) {
      let url = config.isometricUrl;

      url = url + `?message=${panelProps.messageId}&uuid=${panelProps.uuid}`;
      window.open(url, "_blank");
    }
  };

  const handleCheckedChange = (checked: boolean) => {
    const params = new URLSearchParams(location.search);
    if (checked) {
      params.set("unified_model_mode", "diagram");
    } else {
      params.delete("unified_model_mode");
    }
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  const handleCopyAll = () => {
    if (typeof message === "string") {
      navigator.clipboard.writeText(message);
      setCopied(true);
      setForceOpen(true);
      setTimeout(() => {
        setCopied(false), setForceOpen(false);
      }, 1500);
    }
  };

  // BREEZEAI-785: open the FRD's standalone HTML render in a new tab, via a
  // Blob URL.
  const handleOpenHtml = () => {
    if (!htmlContent) {
      toast.error("No HTML content available");
      return;
    }
    try {
      const blob = new Blob([htmlContent], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (error) {
      console.error("Error opening HTML preview:", error);
      toast.error("Failed to open HTML preview");
    }
  };

  // Open a plain-Markdown FRD in a new tab: render the same markdown to a
  // self-contained HTML page (same parser as the panel + inline CSS) and open
  // it as a Blob. Used for artifacts that have no standalone HTML render.
  const handleOpenMarkdown = () => {
    if (typeof message !== "string" || !message) {
      toast.error("No content available");
      return;
    }
    try {
      const rendered = renderToStaticMarkup(
        // Same plugin set as the in-app panel (markdown-renderer.tsx): rehypeRaw
        // renders the FRD's embedded HTML anchors (e.g. <a id="fr-001--...">)
        // instead of printing them as literal text. The `code` override turns
        // ```mermaid fences into <div class="mermaid" data-src> so the embedded
        // controller (below) renders + wraps each with a code toggle + zoom pill.
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
          components={{
            code({ className, children }) {
              if (/\blanguage-mermaid\b/.test(className || "")) {
                const src = String(children).replace(/\n+$/, "");
                return (
                  <div className="mermaid" data-src={src}>
                    {src}
                  </div>
                );
              }
              return <code className={className}>{children}</code>;
            },
          }}
        >
          {message}
        </ReactMarkdown>
      );
      // Diagram viewer styles (match the in-app viewer: white card, </> code
      // toggle, +/- zoom pill, grab-to-pan).
      const diagramCss =
        ".dwrap{position:relative;border:1px solid #e2e8f0;border-radius:12px;background:#fff;overflow:auto;max-height:80vh;margin:1.5rem 0;cursor:grab;}" +
        ".dview{display:flex;justify-content:center;padding:20px;}" +
        ".dview svg{max-width:100%;max-height:74vh;width:auto;height:auto;}" +
        ".dcode{margin:0;padding:16px;font-family:ui-monospace,monospace;font-size:13px;white-space:pre;overflow:auto;color:#334155;}" +
        ".dbar{position:absolute;top:10px;right:10px;z-index:10;}" +
        ".dpill{position:absolute;bottom:12px;right:12px;display:flex;flex-direction:column;border:1px solid #e2e8f0;border-radius:9999px;overflow:hidden;background:#fff;z-index:10;}" +
        ".dbtn{background:#fff;border:1px solid #e2e8f0;border-radius:8px;width:30px;height:30px;color:#64748b;cursor:pointer;font-size:13px;line-height:1;display:flex;align-items:center;justify-content:center;}" +
        ".dpill .dbtn{border:none;border-radius:0;}.dpill .dbtn+.dbtn{border-top:1px solid #e2e8f0;}";
      // Vanilla controller (no backticks / ${} so it survives string concat).
      const controller = [
        "mermaid.initialize({startOnLoad:false,theme:'neutral',securityLevel:'loose',maxTextSize:100000,htmlLabels:true,fontFamily:'system-ui, -apple-system, Arial, sans-serif',fontSize:13,flowchart:{htmlLabels:true,curve:'basis',nodeSpacing:60,rankSpacing:60,padding:20,useMaxWidth:false}});",
        "document.addEventListener('DOMContentLoaded',function(){",
        "  document.querySelectorAll('.mermaid').forEach(function(el,i){",
        "    var src=el.getAttribute('data-src')||el.textContent;",
        "    mermaid.render('m'+i,src).then(function(r){",
        "      var box=document.createElement('div');box.className='dwrap';",
        "      var view=document.createElement('div');view.className='dview';view.innerHTML=r.svg;",
        "      var code=document.createElement('pre');code.className='dcode';code.textContent=src;code.style.display='none';",
        "      var bar=document.createElement('div');bar.className='dbar';",
        "      var cb=document.createElement('button');cb.className='dbtn';cb.textContent='</>';",
        "      var pill=document.createElement('div');pill.className='dpill';",
        "      var zi=document.createElement('button');zi.className='dbtn';zi.textContent='+';",
        "      var zo=document.createElement('button');zo.className='dbtn';zo.textContent='\\u2212';",
        "      var z=1;function ap(){view.style.zoom=z;}",
        "      zi.onclick=function(){z=Math.min(4,Math.round(z*125)/100);ap();};",
        "      zo.onclick=function(){z=Math.max(0.4,Math.round(z*80)/100);ap();};",
        "      cb.onclick=function(){var s=code.style.display!=='none';code.style.display=s?'none':'block';view.style.display=s?'flex':'none';};",
        "      var d=false,sx,sy,sl,st;",
        "      box.addEventListener('mousedown',function(e){d=true;sx=e.clientX;sy=e.clientY;sl=box.scrollLeft;st=box.scrollTop;box.style.cursor='grabbing';});",
        "      box.addEventListener('mousemove',function(e){if(!d)return;box.scrollLeft=sl-(e.clientX-sx);box.scrollTop=st-(e.clientY-sy);});",
        "      window.addEventListener('mouseup',function(){d=false;box.style.cursor='grab';});",
        "      bar.appendChild(cb);pill.appendChild(zi);pill.appendChild(zo);",
        "      box.appendChild(bar);box.appendChild(pill);box.appendChild(view);box.appendChild(code);",
        "      el.replaceWith(box);",
        "    }).catch(function(){el.textContent='Diagram failed to render';});",
        "  });",
        "});",
      ].join("\n");
      const doc = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">`
        + `<meta name="viewport" content="width=device-width, initial-scale=1.0">`
        + `<title>Functional Requirements Document</title>`
        + `<style>${MARKDOWN_TAB_CSS}\n${diagramCss}</style></head>`
        + `<body><article class="markdown-body">${rendered}</article>`
        + `<script src="https://cdn.jsdelivr.net/npm/mermaid@11/dist/mermaid.min.js"></script>`
        + `<script>${controller}</script>`
        + `</body></html>`;
      const blob = new Blob([doc], { type: "text/html" });
      window.open(URL.createObjectURL(blob), "_blank");
    } catch (error) {
      console.error("Error opening markdown preview:", error);
      toast.error("Failed to open markdown preview");
    }
  };

  // Download an in-memory string as a file (Blob URL → click → revoke).
  // Kept plain so the download flow can be reused for Markdown, HTML, or any
  // other artifact payload without duplicating Blob wiring.
  const triggerBlobDownload = (
    body: string,
    mime: string,
    extension: string,
  ) => {
    try {
      const blob = new Blob([body], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement("a");
      link.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      link.download = `artifact_${timestamp}.${extension}`;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Document downloaded successfully");
    } catch (error) {
      console.error("Error downloading document:", error);
      toast.error("Failed to download document");
    }
  };

  const handleDownload = () => {
    // View already fetched `content` + `metadata.htmlContent`, so both are in
    // memory here: `htmlContent` (the standalone HTML render) when the artifact
    // has one, and `message` (the Markdown FRD) otherwise. No refetch needed.
    if (htmlContent) {
      triggerBlobDownload(htmlContent, "text/html", "html");
      return;
    }
    if (typeof message === "string" && message) {
      triggerBlobDownload(message, "text/markdown", "md");
      return;
    }
    toast.error("No content available to download");
  };


  // Don't show right panel for drawer-only panel types
  if (panelType && ["UNIFIED_MODEL", "METADATA_VIEWER"].includes(panelType)) {
    return null;
  }

  return (
    <TooltipProvider>
      <section className="w-full h-full flex flex-col bg-surface overflow-hidden">
        <div className="flex justify-end items-center p-1.5">
          {panelType === "UNIFIED_MODEL" &&
            panelProps?.mode === "unified_model" && (
              <div className="flex gap-2 mr-4 text-white items-center">
                <Switch
                  checked={isUnifiedModelDiagramModeEnabled}
                  onCheckedChange={handleCheckedChange}
                />
                <span className="inline-block relative">
                  <span className="invisible">diagram mode</span>
                  <span className="absolute inset-0">
                    {isUnifiedModelDiagramModeEnabled
                      ? "diagram mode"
                      : "model mode"}
                  </span>
                </span>
              </div>
            )}
          {panelProps?.mode === "diagram" && (
            <Button
              onClick={handleOpenVisualEditor}
              className="mr-4 float-right bg-primary hover:bg-primary-dark text-white"
            >
              Open in visual editor
            </Button>
          )}
          {typeof panelType === "string" &&
            ["MARKDOWN_RENDER", "GHERKIN_SCRIPT"].includes(panelType) &&
            message &&
            message !== "processing" && (
              <>
                {/* Open in new tab. HTML artifacts open the standalone HTML
                    viewer (metadata.htmlContent[0].html); Markdown functional
                    docs open the rendered markdown as a self-contained page. */}
                {(htmlContent || panelType === "MARKDOWN_RENDER") && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => {
                          if (isPreviewTruncated) return;
                          if (htmlContent) handleOpenHtml();
                          else handleOpenMarkdown();
                        }}
                        aria-disabled={isPreviewTruncated}
                        className={`mr-2 p-1 rounded flex items-center gap-1 text-muted ${
                          isPreviewTruncated
                            ? "opacity-40 cursor-not-allowed hover:bg-transparent"
                            : "hover:bg-surface-hover hover:text-white"
                        }`}
                      >
                        <ExternalLink className="w-4 h-4" />
                        <span className="text-sm">Open</span>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      {isPreviewTruncated
                        ? "Preview is truncated — download the file to view the full contents."
                        : htmlContent
                          ? "Open HTML in new tab"
                          : "Open markdown in new tab"}
                    </TooltipContent>
                  </Tooltip>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={handleDownload}
                      aria-label="Download"
                      title="Download"
                      className="mr-2 p-1 rounded hover:bg-surface-hover text-muted hover:text-white"
                    >
                      <Download className="w-4 h-4" aria-hidden="true" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="left">
                    Download
                  </TooltipContent>
                </Tooltip>
                <Tooltip open={forceOpen} onOpenChange={setForceOpen}>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => {
                        handleCopyAll();
                        setForceOpen(true);
                        setCopied(true);
                        setTimeout(() => {
                          setCopied(false);
                          setForceOpen(false);
                        }, 1500);
                      }}
                      aria-label={copied ? "Copied to clipboard" : "Copy to clipboard"}
                      title={copied ? "Copied!" : "Copy to clipboard"}
                      className="mr-2 p-1 rounded hover:bg-surface-hover text-muted hover:text-white"
                    >
                      {copied ? (
                        <Check className="w-4 h-4 text-success" aria-hidden="true" />
                      ) : (
                        <ClipboardCopy className="w-4 h-4" aria-hidden="true" />
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="left">
                    {copied ? "Copied!" : "Copy all"}
                  </TooltipContent>
                </Tooltip>
              </>
            )}
          <button
            onClick={() => setPanelType(null)}
            aria-label="Close"
            title="Close"
            className="float-right p-2 rounded-md hover:bg-surface-hover focus:bg-surface-hover focus:outline-none transition-all duration-200 cursor-pointer active:scale-95"
          >
            <X aria-hidden="true" className="h-5 w-5 text-muted hover:text-text-primary focus:text-text-primary transition-colors duration-200" />
          </button>
        </div>
        {panelType && RIGHT_PANEL[panelType as keyof typeof RIGHT_PANEL]
          ? RIGHT_PANEL[panelType as keyof typeof RIGHT_PANEL]
          : null}
      </section>
    </TooltipProvider>
  );
}
